import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import {
  FiatWalletAccountState,
  OffRampBridgeKycStatus,
} from '#src/__generated__/globalTypes.ts';
import { useQuery } from '#src/hooks/graphql/useQuery.ts';

import type {
  OffRampOnboardingStateQuery,
  OffRampOnboardingStateQueryVariables,
} from './__generated__/useOffRampOnboardingState.graphql.ts';

const OFF_RAMP_ONBOARDING_STATE_QUERY = gql`
  query OffRampOnboardingStateQuery {
    currentUser {
      slug
      myAccounts {
        id
        accountable {
          ... on Node {
            id
          }
          ... on PrivateFiatWalletAccount {
            id
            state
          }
        }
      }
      offRampBridgeAccount {
        id
        kycStatus
        createdAt
      }
    }
  }
` as TypedDocumentNode<
  OffRampOnboardingStateQuery,
  OffRampOnboardingStateQueryVariables
>;

type BridgeAccount = NonNullable<
  NonNullable<
    OffRampOnboardingStateQuery['currentUser']
  >['offRampBridgeAccount']
>;

export type OffRampOnboardingState =
  | { status: 'loading' }
  | { status: 'signedOut' }
  | { status: 'mangopayKycRequired' }
  | { status: 'notEnrolled' }
  | { status: 'kycPending'; account: BridgeAccount }
  | { status: 'active'; account: BridgeAccount }
  | { status: 'terminalFailure'; account: BridgeAccount };

const TERMINAL_FAILURE_STATUSES: OffRampBridgeKycStatus[] = [
  OffRampBridgeKycStatus.REJECTED,
  OffRampBridgeKycStatus.BLOCKED,
  OffRampBridgeKycStatus.CLOSED,
];

const toState = (
  account: BridgeAccount | null | undefined
): OffRampOnboardingState => {
  if (!account) return { status: 'notEnrolled' };
  if (account.kycStatus === OffRampBridgeKycStatus.ACTIVE) {
    return { status: 'active', account };
  }
  if (TERMINAL_FAILURE_STATUSES.includes(account.kycStatus)) {
    return { status: 'terminalFailure', account };
  }
  return { status: 'kycPending', account };
};

export const useOffRampOnboardingState = ({
  pollInterval,
}: { pollInterval?: number } = {}) => {
  const { data, loading, refetch } = useQuery(OFF_RAMP_ONBOARDING_STATE_QUERY, {
    pollInterval,
  });

  const { currentUser } = data ?? {};

  let state: OffRampOnboardingState;
  if (loading && !data) {
    state = { status: 'loading' };
  } else if (!currentUser) {
    state = { status: 'signedOut' };
  } else {
    const bridgeState = toState(currentUser.offRampBridgeAccount);
    // Bridge enrollment auto-provisions the Mangopay IBAN deposit account, which
    // the backend only allows for a validated-owner cash wallet. Gate before
    // enrollment so users complete Mangopay KYC first, rather than dead-ending at
    // the address step. An existing Bridge account means they're already past it.
    const fiatWalletState = (currentUser.myAccounts ?? [])
      .map(({ accountable }) =>
        accountable && 'state' in accountable ? accountable.state : null
      )
      .find(Boolean);

    state =
      bridgeState.status === 'notEnrolled' &&
      fiatWalletState !== FiatWalletAccountState.VALIDATED_OWNER
        ? { status: 'mangopayKycRequired' }
        : bridgeState;
  }

  return { state, refetch };
};
